import { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { ICollectionSearchResults, ICollection } from "services/types";
import { CollectionTile } from "./CollectionTile";
import { RawCollectionTile } from "./RawCollectionTile";
import { GifBlockPlaceholder } from "components/UI/Skeleton";
import { apiCollections } from "services";
import { routes } from "routes";

import LikedIcon from "components/icons/LikedIcon.svg";

import "./CollectionsList.scss";

const PAGE_SIZE = 50;

export const CollectionsList = (props: {
  userName: string;
  showSaved: boolean;
}): JSX.Element => {
  const [pagesRead, setPagesRead] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [items, setItems] = useState<ICollection[]>([]);
  const [error, setError] = useState<string|null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetch = useCallback((page: number) => {
    setLoading(true);
    apiCollections.getUserCollections({
      userId: props.userName,
      page: page,
      count: PAGE_SIZE,
    }).then((data: ICollectionSearchResults) => {
      if (data.page <= pagesRead) {
        console.debug(`[collections] Read user=${props.userName} page=${data.page} of=${data.pages} (again).`);
      } else {
        console.debug(`[collections] Read user=${props.userName} page=${data.page} of=${data.pages}.`);

        setItems(old => [...old, ...data.collections]);
        setPagesRead(data.page);
        setHasMore(data.page < data.pages);
        setError(null);
      }
    }).catch((e: any) => {
      console.error(`[collections] Error reading data: ${e}.`);
      setError(e.message);
    }).finally(() => {
      setLoading(false);
    });
  }, [pagesRead, props.userName]);

  useEffect(() => {
    if (!ready) {
      setReady(true);
      fetch(1);
    }
  }, [ready, fetch]);

  const fetchMore = () => {
    fetch(pagesRead + 1);
  };

  const renderCollectionItems = () => {
    if (!items.length && loading) {
      // eslint-disable-next-line no-magic-numbers
      return new Array(12).fill("").map((___, index) => (
        <div className="collectionPlaceholder" key={index}>
          <GifBlockPlaceholder/>
          <div className="title"></div>
        </div>
      ));
    }

    if (!items.length && !loading) {
      return <p>No saved items</p>;
    }

    return (
      <>
        {props.showSaved && (
          <RawCollectionTile
            link={routes.favourites()}
            thumbnail={LikedIcon}
            published={false}
            label="Likes"
          />
        )}
        {items.map((item: ICollection) => (
          <CollectionTile
            key={item.folderId}
            userName={props.userName}
            collection={item}
          />
        ))}
      </>
    );
  };

  if (error) {
    return <p>Error reading collections.</p>;
  }

  return (
    <div className="collections">
      <InfiniteScroll
        dataLength={items.length}
        hasMore={hasMore}
        next={fetchMore}
        loader={<></>}
      >
        <div className="tiles">
          {renderCollectionItems()}
        </div>
      </InfiniteScroll>
    </div>
  );
};
